import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Package, Briefcase, FileText, Wrench, Activity, RefreshCw, ExternalLink,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map activity type to icon + color
const TYPE_META = {
  product: { icon: Package, color: 'bg-blue-100 text-blue-700', label: 'Produkt' },
  application: { icon: Briefcase, color: 'bg-emerald-100 text-emerald-700', label: 'Ansökan' },
  quote: { icon: FileText, color: 'bg-amber-100 text-amber-700', label: 'Offert' },
  specialtank: { icon: Wrench, color: 'bg-purple-100 text-purple-700', label: 'Specialtank' },
};

const formatRelativeTime = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'just nu';
  if (diffMin < 60) return `${diffMin} min sedan`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH} h sedan`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD} d sedan`;
  return date.toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' });
};

const RecentActivityWidget = ({ limit = 8 }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchActivity = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/recent-activity?limit=${limit}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Kunde inte hämta aktivitet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="recent-activity-widget"
      className="bg-white border border-stone-200 mb-8"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#321a27] text-white flex items-center justify-center">
            <Activity className="w-4 h-4" />
          </div>
          <div>
            <h2 className="font-semibold text-stone-900">Senaste aktivitet</h2>
            <p className="text-xs text-stone-500">
              Senaste {limit} händelserna i affären
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={fetchActivity}
          disabled={loading}
          className="text-stone-500 hover:text-[#321a27] transition-colors p-2 disabled:opacity-50"
          title="Uppdatera"
          data-testid="recent-activity-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error ? (
        <div className="p-6 text-center text-sm text-red-600">{error}</div>
      ) : loading && items.length === 0 ? (
        <div className="divide-y divide-stone-100">
          {[1, 2, 3].map((n) => (
            <div key={n} className="p-4 animate-pulse flex gap-3">
              <div className="w-9 h-9 bg-stone-200 rounded" />
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-stone-200 w-3/5" />
                <div className="h-2 bg-stone-100 w-2/5" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-sm text-stone-500">
          Inga händelser ännu.
        </div>
      ) : (
        <ul className="divide-y divide-stone-100 max-h-[420px] overflow-y-auto">
          {items.map((item, idx) => {
            const meta = TYPE_META[item.type] || TYPE_META.product;
            const Icon = meta.icon;
            const isExternal = item.link && item.link.startsWith('/produkter');
            return (
              <li
                key={`${item.type}-${idx}-${item.timestamp}`}
                className="px-5 py-3 hover:bg-stone-50 transition-colors flex items-start gap-3 group"
                data-testid={`activity-item-${idx}`}
              >
                <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center ${meta.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-stone-900 truncate">
                      {item.title}
                    </p>
                    <span className="text-xs text-stone-400 flex-shrink-0 whitespace-nowrap">
                      {formatRelativeTime(item.timestamp)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-stone-500 truncate">
                      {item.subtitle}
                    </span>
                    {item.status && (
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${
                        item.status === 'new'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-stone-100 text-stone-600'
                      }`}>
                        {item.status === 'new' ? 'Ny' : item.status}
                      </span>
                    )}
                    {isExternal && item.link && (
                      <Link
                        to={item.link}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-[#321a27] hover:underline inline-flex items-center gap-0.5"
                      >
                        Visa <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default RecentActivityWidget;
